import './index.css';
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { localeManager, t } from '../../services/locale';
import { themeManager } from '../../services/theme';
import type { MeasureConfig, MeasurePanelOptions, MeasurePrecision, MeasureResult, MeasureUnit, ClearHeightDirection, ClearHeightSelectType } from './types';
import { MEASURE_TYPES, MEASURE_MODES_ORDERED, getValueType, type MeasureMode, type MeasureValueType } from '../../types/measure';

/**
 * 测量面板组件（只做 UI，不实现真实测量）
 *
 * 组件职责：
 * - 展示 8 种测量方式按钮（默认 4 个，可展开/收起）
 * - 维护当前选中的测量方式（current mode）
 * - 展示测量结果（由外部 setResult 注入）
 * - 提供 “删除全部 / 设置” 的 UI 与对外方法（暂不实现真实逻辑，仅回调/占位）
 *
 * 注意：
 * - 所有用户可见文本必须通过 t(key) 获取（国际化强制要求）
 * - 组件需要订阅主题/语言变更，并在 destroy 时清理订阅
 */
export class MeasurePanel implements IBimComponent {
  public element: HTMLElement;

  private options: MeasurePanelOptions;
  private activeMode: MeasureMode | null;
  private isExpanded: boolean;
  private result: MeasureResult | null = null;

  // 净高模式专属状态
  private clearHeightDirection: ClearHeightDirection = 0; // 默认朝下
  private clearHeightSelectType: ClearHeightSelectType = 'point'; // 默认选择点

  /**
   * 测量配置（单位/精度）
   * 说明：
   * - 你要求：创建 MeasurePanel 不传入单位和精度
   * - 默认值维护在组件内部
   * - 初始化时优先读取缓存（localStorage），否则使用默认值
   */
  private config: MeasureConfig;

  /** 设置面板的临时配置（用于“取消”回滚） */
  private draftConfig: MeasureConfig | null = null;

  /** 当前视图：主面板 / 设置面板 */
  private view: 'main' | 'settings' = 'main';

  /** 缓存 key（默认全局） */
  private static readonly CONFIG_CACHE_KEY = 'bim-engine:measure:config';

  /** 默认配置（由组件内部维护） */
  private static readonly DEFAULT_CONFIG: MeasureConfig = {
    unit: 'mm',
    precision: 2
  };

  // DOM 引用（便于局部更新，减少频繁 querySelector）
  private toolButtons: Map<MeasureMode, HTMLButtonElement> = new Map();
  private toggleBtn!: HTMLButtonElement;
  private toggleTextEl!: HTMLElement;
  private mainValueRowEl!: HTMLElement;
  private mainValueValueEl!: HTMLElement;
  private mainValueLabelEl!: HTMLElement;
  private mainNumberEl!: HTMLElement;
  private mainUnitEl!: HTMLElement;
  private xyzBoxEl!: HTMLElement;
  private xyzXEl!: HTMLElement;
  private xyzYEl!: HTMLElement;
  private xyzZEl!: HTMLElement;
  private clearBtn!: HTMLButtonElement;
  private settingsBtn!: HTMLButtonElement;

  // 净高模式选项 DOM
  private modeOptionsEl!: HTMLElement;
  private directionBtns: Map<ClearHeightDirection, HTMLButtonElement> = new Map();
  private selectTypeBtns: Map<ClearHeightSelectType, HTMLButtonElement> = new Map();

  // Settings DOM
  private mainViewEl!: HTMLElement;
  private settingsViewEl!: HTMLElement;
  private unitSelectEl!: HTMLSelectElement;
  private precisionSelectEl!: HTMLSelectElement;
  private saveSettingsBtn!: HTMLButtonElement;
  private cancelSettingsBtn!: HTMLButtonElement;

  // 订阅清理
  private unsubscribeLocale: (() => void) | null = null;
  private unsubscribeTheme: (() => void) | null = null;

  /**
   * 构造函数
   * @param options 组件配置
   */
  constructor(options: MeasurePanelOptions = {}) {
    this.options = options;
    this.activeMode = options.defaultMode ?? 'distance';
    this.isExpanded = options.defaultExpanded ?? false;

    // 读取配置：优先缓存，否则默认
    this.config = this.loadConfigFromCache() ?? { ...MeasurePanel.DEFAULT_CONFIG };

    this.element = this.createDom();
  }

  /**
   * 初始化组件（实现 IBimComponent）
   */
  public init(): void {
    // 订阅语言变更：更新所有文本/提示
    this.unsubscribeLocale = localeManager.subscribe(() => {
      this.setLocales();
    });

    // 订阅主题变更：更新 CSS 变量（如需要）
    this.unsubscribeTheme = themeManager.subscribe((theme) => {
      this.setTheme(theme);
    });

    // 初始应用
    this.setLocales();
    this.setTheme(themeManager.getTheme());

    // 初始渲染状态（按钮显隐、选中态、结果区）
    this.applyExpandedState();
    this.applyActiveModeState();
    this.applyClearHeightOptionsState();
    this.applyViewState();
    this.renderResult();

    // 触发初始测量模式的回调（让外部知道默认激活了哪个模式）
    if (this.options.onModeChange && this.activeMode) {
      this.options.onModeChange(this.activeMode);
    }
  }

  /**
   * 设置主题（实现 IBimComponent）
   * @param theme 主题配置
   */
  public setTheme(theme: ThemeConfig): void {
    const style = this.element.style;

    style.setProperty('--bim-bg-inset', theme.bgInset ?? '#152232');
    style.setProperty('--bim-bg-elevated', theme.bgElevated ?? '#1f2d3e');
    style.setProperty('--bim-border-default', theme.borderDefault ?? '#334155');
    style.setProperty('--bim-border-strong', theme.borderStrong ?? '#475569');
    style.setProperty('--bim-divider', theme.divider ?? '#334155');
    
    style.setProperty('--bim-primary', theme.primary ?? '#3b82f6');
    style.setProperty('--bim-primary-hover', theme.primaryHover ?? '#60a5fa');
    style.setProperty('--bim-primary-subtle', theme.primarySubtle ?? 'rgba(59, 130, 246, 0.15)');
    style.setProperty('--bim-danger', theme.danger ?? '#ef4444');
    
    style.setProperty('--bim-text-primary', theme.textPrimary ?? '#ffffff');
    style.setProperty('--bim-text-secondary', theme.textSecondary ?? '#94a3b8');
    style.setProperty('--bim-text-tertiary', theme.textTertiary ?? '#64748b');
    style.setProperty('--bim-text-inverse', theme.textInverse ?? '#152232');
    
    style.setProperty('--bim-icon-default', theme.iconDefault ?? '#ffffff');
    
    style.setProperty('--bim-component-bg-hover', theme.componentBgHover ?? 'rgba(248, 250, 252, 0.06)');
  }

  /**
   * 设置语言（实现 IBimComponent）
   */
  public setLocales(): void {
    // 1) 更新按钮 tooltip（图标占位时，tooltip 是主要的可读文本）
    for (const [mode, btn] of this.toolButtons.entries()) {
      btn.title = t(this.getModeI18nKey(mode));
      btn.setAttribute('aria-label', btn.title);
    }

    // 2) 更新展开/收起按钮 tooltip
    this.toggleBtn.title = this.isExpanded ? t('measure.actions.collapse') : t('measure.actions.expand');
    this.toggleBtn.setAttribute('aria-label', this.toggleBtn.title);

    // 2.1) 更新展开/收起按钮可见文本（你要求的“文字提示”）
    if (this.toggleTextEl) {
      this.toggleTextEl.textContent = this.toggleBtn.title;
    }

    // 3) 更新底部按钮文本/tooltip
    this.clearBtn.textContent = t('measure.actions.clearAll');
    this.settingsBtn.title = t('measure.actions.settings');
    this.settingsBtn.setAttribute('aria-label', this.settingsBtn.title);

    // 4) 主值 label（随模式变化）
    if (this.activeMode) {
      this.mainValueLabelEl.textContent = t(this.getModeValueLabelI18nKey(this.activeMode));
    }

    // 6) XYZ label（使用 key）
    // 这里 label 在 createDom 已经是固定文本节点，直接用 setText 更新更直观
    // 但为了减少 DOM 结构复杂度，我们把 label 写在 createDom 里，通过 data-key 更新
    const labelNodes = this.element.querySelectorAll<HTMLElement>('[data-i18n-key]');
    labelNodes.forEach((node) => {
      const key = node.dataset.i18nKey;
      if (key) node.textContent = t(key);
    });

    // 7) 设置面板文本
    this.saveSettingsBtn.textContent = t('measure.settings.save');
    this.cancelSettingsBtn.textContent = t('measure.settings.cancel');
  }

  /**
   * 销毁组件（实现 IBimComponent）
   */
  public destroy(): void {
    // 清理订阅
    if (this.unsubscribeLocale) {
      this.unsubscribeLocale();
      this.unsubscribeLocale = null;
    }
    if (this.unsubscribeTheme) {
      this.unsubscribeTheme();
      this.unsubscribeTheme = null;
    }

    // 清理事件监听：由于本组件的监听都绑定在创建时的具体按钮上，
    // 且按钮会随 element 一起被 GC，这里不做逐个 removeEventListener（可读性优先）

    // 移除 DOM
    this.element.remove();
  }

  // ==========================
  // 对外 API（给 Manager / 外部业务调用）
  // ==========================

  /**
   * 获取当前测量方式
   */
  public getActiveMode(): MeasureMode | null {
    return this.activeMode;
  }

  public switchMode(mode: MeasureMode, triggerCallback: boolean = true): void {
    this.setActiveMode(mode, triggerCallback);
  }

  public setActiveMode(mode: MeasureMode, triggerCallback: boolean = true): void {
    if (this.activeMode === mode) return;
    this.activeMode = mode;
    this.applyActiveModeState();
    this.applyClearHeightOptionsState();

    this.mainValueLabelEl.textContent = t(this.getModeValueLabelI18nKey(this.activeMode));

    if (triggerCallback && this.options.onModeChange) {
      this.options.onModeChange(mode);
    }

    this.renderResult();

    if (this.options.onExpandedChange) {
      this.options.onExpandedChange(this.isExpanded);
    }
  }

  /**
   * 设置测量结果（由外部注入）
   * @param result 测量结果；传 null 表示清空
   */
  public setResult(result: MeasureResult | null): void {
    this.result = result;
    this.renderResult();
  }

  /**
   * 删除全部（只做 UI 状态清空 + 回调）
   */
  public clearAll(): void {
    // 先清空结果显示
    this.result = null;
    this.renderResult();

    // 通知外部
    if (this.options.onClearAll) {
      this.options.onClearAll();
    }
  }

  /**
   * 打开设置（本次只预留方法/回调）
   */
  public openSettings(): void {
    // 进入设置面板（组件内部逻辑）
    this.enterSettingsView();

    // 仍然保留回调（如果外部想监听）
    if (this.options.onSettings) {
      this.options.onSettings();
    }
  }

  /**
   * 获取当前测量配置
   */
  public getConfig(): MeasureConfig {
    return { ...this.config };
  }

  /**
   * 设置测量配置（可选对外调用）
   * @param partial 部分更新
   * @param persist 是否写入缓存（默认 false）
   */
  public setConfig(partial: Partial<MeasureConfig>, persist: boolean = false): void {
    const next: MeasureConfig = {
      unit: partial.unit ?? this.config.unit,
      precision: partial.precision ?? this.config.precision
    };
    this.config = next;
    if (persist) {
      this.saveConfigToCache(next);
    }

    // 配置变化会影响数值显示（单位/精度）
    this.renderResult();

    // 如果当前在设置面板，表单也需要同步
    if (this.view === 'settings') {
      this.syncSettingsFormFromConfig(next);
    }
  }

  /**
   * 展开 / 收起（可选对外调用）
   * @param expanded 是否展开
   */
  public setExpanded(expanded: boolean): void {
    if (this.isExpanded === expanded) return;
    this.isExpanded = expanded;
    this.applyExpandedState();
    this.setLocales(); // 更新 tooltip（展开/收起）

    // 通知外部：用于重新计算 Dialog 高度
    if (this.options.onExpandedChange) {
      this.options.onExpandedChange(this.isExpanded);
    }
  }

  /**
   * 获取是否展开
   */
  public getExpanded(): boolean {
    return this.isExpanded;
  }

  // ==========================
  // 内部实现
  // ==========================

  private createDom(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'bim-measure-panel';

    // 主视图容器（默认显示）
    this.mainViewEl = document.createElement('div');
    this.mainViewEl.className = 'bim-measure-main';

    // 顶部：工具按钮区
    const toolsBox = document.createElement('div');
    toolsBox.className = 'bim-measure-tools';

    const grid = document.createElement('div');
    grid.className = 'bim-measure-tool-grid';

    const fallbackCircleIconSvg = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9"></circle>
      </svg>
    `;

    for (let i = 0; i < MEASURE_MODES_ORDERED.length; i++) {
      const mode = MEASURE_MODES_ORDERED[i];
      const config = MEASURE_TYPES[mode];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bim-measure-tool-btn';
      btn.dataset.mode = mode;

      const icon = document.createElement('span');
      icon.className = 'bim-measure-tool-icon';
      icon.innerHTML = config.icon || fallbackCircleIconSvg;
      btn.appendChild(icon);

      // 点击切换模式
      btn.addEventListener('click', () => {
        this.setActiveMode(mode);
      });

      // 先不在这里设置 title/text（统一交给 setLocales）
      this.toolButtons.set(mode, btn);
      grid.appendChild(btn);
    }

    toolsBox.appendChild(grid);

    // 展开/收起按钮（箭头）
    const toggleBox = document.createElement('div');
    toggleBox.className = 'bim-measure-toggle';

    this.toggleBtn = document.createElement('button');
    this.toggleBtn.type = 'button';
    this.toggleBtn.className = 'bim-measure-toggle-btn';
    // 展开/收起按钮：更小，并带文字提示（展开/收起）
    // 注意：文本内容由 setLocales() 统一更新，这里先放一个占位容器
    this.toggleTextEl = document.createElement('span');
    this.toggleTextEl.className = 'bim-measure-toggle-text';
    const toggleIconEl = document.createElement('span');
    toggleIconEl.className = 'bim-measure-toggle-icon';
    toggleIconEl.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M7 10l5 5 5-5z"></path>
      </svg>
    `;
    this.toggleBtn.appendChild(this.toggleTextEl);
    this.toggleBtn.appendChild(toggleIconEl);
    this.toggleBtn.addEventListener('click', () => {
      this.isExpanded = !this.isExpanded;
      this.applyExpandedState();
      this.setLocales(); // 更新 tooltip（展开/收起）

      // 通知外部：用于重新计算 Dialog 高度
      if (this.options.onExpandedChange) {
        this.options.onExpandedChange(this.isExpanded);
      }
    });

    toggleBox.appendChild(this.toggleBtn);
    toolsBox.appendChild(toggleBox);
    this.mainViewEl.appendChild(toolsBox);

    // 净高模式选项区（朝向 + 选择对象）
    this.modeOptionsEl = this.createModeOptionsDom();
    this.mainViewEl.appendChild(this.modeOptionsEl);

    // 中部：结果区
    const resultBox = document.createElement('div');
    resultBox.className = 'bim-measure-result';

    // 主结果值（随模式变化）
    const mainValueRow = document.createElement('div');
    mainValueRow.className = 'bim-measure-row';
    this.mainValueRowEl = mainValueRow;
    const mainValueLabel = document.createElement('span');
    mainValueLabel.className = 'label';
    this.mainValueLabelEl = mainValueLabel;
    const mainValueValue = document.createElement('span');
    mainValueValue.className = 'value';
    this.mainValueValueEl = mainValueValue;

    // 主值拆分：数值（黄色）+ 单位（普通色）
    // 这样可以满足：
    // 1) 只让“数据”变黄，单位不变色
    // 2) 没有数据时展示 `-- 单位`
    this.mainNumberEl = document.createElement('span');
    this.mainNumberEl.className = 'bim-measure-main-number';
    this.mainUnitEl = document.createElement('span');
    this.mainUnitEl.className = 'bim-measure-main-unit';
    this.mainValueValueEl.appendChild(this.mainNumberEl);
    this.mainValueValueEl.appendChild(document.createTextNode(' '));
    this.mainValueValueEl.appendChild(this.mainUnitEl);
    mainValueRow.appendChild(mainValueLabel);
    mainValueRow.appendChild(mainValueValue);
    resultBox.appendChild(mainValueRow);

    // XYZ
    const xyzBox = document.createElement('div');
    xyzBox.className = 'bim-measure-xyz';
    this.xyzBoxEl = xyzBox;

    const makeXyzRow = (labelKey: string, valueClassName: string, valueElSetter: (el: HTMLElement) => void) => {
      const row = document.createElement('div');
      row.className = 'bim-measure-row';
      const label = document.createElement('span');
      label.className = 'label';
      label.dataset.i18nKey = labelKey;
      const value = document.createElement('span');
      value.className = `value ${valueClassName}`;
      valueElSetter(value);
      row.appendChild(label);
      row.appendChild(value);
      return row;
    };

    xyzBox.appendChild(makeXyzRow('measure.labels.x', 'bim-measure-xyz-x', (el) => (this.xyzXEl = el)));
    xyzBox.appendChild(makeXyzRow('measure.labels.y', 'bim-measure-xyz-y', (el) => (this.xyzYEl = el)));
    xyzBox.appendChild(makeXyzRow('measure.labels.z', 'bim-measure-xyz-z', (el) => (this.xyzZEl = el)));
    resultBox.appendChild(xyzBox);

    this.mainViewEl.appendChild(resultBox);

    // 底部：删除全部 + 设置
    const footer = document.createElement('div');
    footer.className = 'bim-measure-footer';

    this.clearBtn = document.createElement('button');
    this.clearBtn.type = 'button';
    this.clearBtn.className = 'bim-measure-clear-btn';
    this.clearBtn.addEventListener('click', () => {
      this.clearAll();
    });

    this.settingsBtn = document.createElement('button');
    this.settingsBtn.type = 'button';
    this.settingsBtn.className = 'bim-measure-settings-btn';
    this.settingsBtn.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.27 7.27 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 1h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.71 7.48a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.83 14.52a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.39.3.6.22l2.39-.96c.5.4 1.05.71 1.63.94l.36 2.54c.04.24.25.42.49.42h3.8c.24 0 .45-.18.49-.42l.36-2.54c.58-.23 1.12-.54 1.63-.94l2.39.96c.22.09.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5z"></path>
      </svg>
    `;
    this.settingsBtn.addEventListener('click', () => {
      this.openSettings();
    });

    footer.appendChild(this.clearBtn);
    footer.appendChild(this.settingsBtn);
    this.mainViewEl.appendChild(footer);

    // 设置视图容器（默认隐藏）
    this.settingsViewEl = this.createSettingsDom();

    root.appendChild(this.mainViewEl);
    root.appendChild(this.settingsViewEl);

    return root;
  }

  /**
   * 创建“设置面板”DOM
   */
  private createSettingsDom(): HTMLElement {
    const box = document.createElement('div');
    box.className = 'bim-measure-settings';

    // 标题
    const title = document.createElement('div');
    title.className = 'bim-measure-settings-title';
    title.dataset.i18nKey = 'measure.settings.title';
    box.appendChild(title);

    // 单位
    const unitRow = document.createElement('div');
    unitRow.className = 'bim-measure-settings-row';
    const unitLabel = document.createElement('div');
    unitLabel.className = 'label';
    unitLabel.dataset.i18nKey = 'measure.settings.unit';
    this.unitSelectEl = document.createElement('select');
    this.unitSelectEl.className = 'bim-measure-settings-select';
    this.unitSelectEl.appendChild(this.makeOption('m'));
    this.unitSelectEl.appendChild(this.makeOption('cm'));
    this.unitSelectEl.appendChild(this.makeOption('mm'));
    this.unitSelectEl.appendChild(this.makeOption('km'));
    unitRow.appendChild(unitLabel);
    unitRow.appendChild(this.unitSelectEl);
    box.appendChild(unitRow);

    // 提示文本：你要求放在“单位”下面
    const hint = document.createElement('div');
    hint.className = 'bim-measure-settings-hint';
    hint.dataset.i18nKey = 'measure.settings.hint';
    box.appendChild(hint);

    // 精度
    const precisionRow = document.createElement('div');
    precisionRow.className = 'bim-measure-settings-row';
    const precisionLabel = document.createElement('div');
    precisionLabel.className = 'label';
    precisionLabel.dataset.i18nKey = 'measure.settings.precision';
    this.precisionSelectEl = document.createElement('select');
    this.precisionSelectEl.className = 'bim-measure-settings-select';
    this.precisionSelectEl.appendChild(this.makePrecisionOption(0));
    this.precisionSelectEl.appendChild(this.makePrecisionOption(1));
    this.precisionSelectEl.appendChild(this.makePrecisionOption(2));
    this.precisionSelectEl.appendChild(this.makePrecisionOption(3));
    precisionRow.appendChild(precisionLabel);
    precisionRow.appendChild(this.precisionSelectEl);
    box.appendChild(precisionRow);

    // 底部按钮
    const actions = document.createElement('div');
    actions.className = 'bim-measure-settings-actions';

    this.saveSettingsBtn = document.createElement('button');
    this.saveSettingsBtn.type = 'button';
    this.saveSettingsBtn.className = 'bim-measure-settings-save';
    this.saveSettingsBtn.addEventListener('click', () => {
      this.saveSettings();
    });

    this.cancelSettingsBtn = document.createElement('button');
    this.cancelSettingsBtn.type = 'button';
    this.cancelSettingsBtn.className = 'bim-measure-settings-cancel';
    this.cancelSettingsBtn.addEventListener('click', () => {
      this.cancelSettings();
    });

    actions.appendChild(this.saveSettingsBtn);
    actions.appendChild(this.cancelSettingsBtn);
    box.appendChild(actions);

    // 初次同步表单值
    this.syncSettingsFormFromConfig(this.config);

    return box;
  }

  /**
   * 创建净高模式选项区 DOM（朝向 + 选择对象）
   */
  private createModeOptionsDom(): HTMLElement {
    const box = document.createElement('div');
    box.className = 'bim-measure-mode-options';

    // 朝向单选组
    const dirGroup = document.createElement('div');
    dirGroup.className = 'bim-measure-radio-group';
    const dirLabel = document.createElement('span');
    dirLabel.className = 'bim-measure-radio-label';
    dirLabel.dataset.i18nKey = 'measure.clearHeight.direction';
    const dirItems = document.createElement('div');
    dirItems.className = 'bim-measure-radio-items';

    const dirDown = this.createRadioItem('measure.clearHeight.directionDown', () => {
      this.setClearHeightDirection(0);
    });
    const dirUp = this.createRadioItem('measure.clearHeight.directionUp', () => {
      this.setClearHeightDirection(1);
    });
    this.directionBtns.set(0, dirDown);
    this.directionBtns.set(1, dirUp);

    dirItems.appendChild(dirDown);
    dirItems.appendChild(dirUp);
    dirGroup.appendChild(dirLabel);
    dirGroup.appendChild(dirItems);
    box.appendChild(dirGroup);

    // 选择对象单选组
    const selGroup = document.createElement('div');
    selGroup.className = 'bim-measure-radio-group';
    const selLabel = document.createElement('span');
    selLabel.className = 'bim-measure-radio-label';
    selLabel.dataset.i18nKey = 'measure.clearHeight.selectType';
    const selItems = document.createElement('div');
    selItems.className = 'bim-measure-radio-items';

    const selPoint = this.createRadioItem('measure.clearHeight.selectPoint', () => {
      this.setClearHeightSelectType('point');
    });
    const selElement = this.createRadioItem('measure.clearHeight.selectElement', () => {
      this.setClearHeightSelectType('element');
    });
    this.selectTypeBtns.set('point', selPoint);
    this.selectTypeBtns.set('element', selElement);

    selItems.appendChild(selPoint);
    selItems.appendChild(selElement);
    selGroup.appendChild(selLabel);
    selGroup.appendChild(selItems);
    box.appendChild(selGroup);

    return box;
  }

  private createRadioItem(i18nKey: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bim-measure-radio-item';
    btn.dataset.i18nKey = i18nKey;
    btn.textContent = t(i18nKey);
    btn.addEventListener('click', onClick);
    return btn;
  }

  private setClearHeightDirection(direction: ClearHeightDirection): void {
    if (this.clearHeightDirection === direction) return;
    this.clearHeightDirection = direction;
    this.applyClearHeightOptionsState();
    this.options.onClearHeightDirectionChange?.(direction);
  }

  private setClearHeightSelectType(selectType: ClearHeightSelectType): void {
    if (this.clearHeightSelectType === selectType) return;
    this.clearHeightSelectType = selectType;
    this.applyClearHeightOptionsState();
    this.options.onClearHeightSelectTypeChange?.(selectType);
  }

  /**
   * 应用净高选项状态（显隐 + 选中态）
   */
  private applyClearHeightOptionsState(): void {
    // 显隐
    if (this.activeMode === 'clearHeight') {
      this.modeOptionsEl.classList.add('is-visible');
    } else {
      this.modeOptionsEl.classList.remove('is-visible');
    }

    // 朝向按钮选中态
    for (const [dir, btn] of this.directionBtns.entries()) {
      if (dir === this.clearHeightDirection) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    }

    // 选择对象按钮选中态
    for (const [type, btn] of this.selectTypeBtns.entries()) {
      if (type === this.clearHeightSelectType) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    }
  }

  private makeOption(unit: MeasureUnit): HTMLOptionElement {
    const opt = document.createElement('option');
    opt.value = unit;
    // 选项显示内容：直接显示单位字符串
    opt.textContent = unit;
    return opt;
  }

  private makePrecisionOption(precision: MeasurePrecision): HTMLOptionElement {
    const opt = document.createElement('option');
    opt.value = String(precision);
    // 显示：0 / 0.0 / 0.00 / 0.000
    opt.textContent = precision === 0 ? '0' : `0.${'0'.repeat(precision)}`;
    return opt;
  }

  /**
   * 进入设置视图：保存一份当前配置作为草稿基线
   */
  private enterSettingsView(): void {
    this.draftConfig = { ...this.config };
    this.view = 'settings';
    this.syncSettingsFormFromConfig(this.config);
    this.applyViewState();
  }

  /**
   * 保存设置：写入 config + 写缓存 + 返回主视图
   */
  private saveSettings(): void {
    const unit = (this.unitSelectEl.value as MeasureUnit) || this.config.unit;
    const precision = (Number(this.precisionSelectEl.value) as MeasurePrecision);
    const next: MeasureConfig = {
      unit,
      precision: this.isValidPrecision(precision) ? precision : this.config.precision
    };

    this.config = next;
    this.saveConfigToCache(next);
    this.draftConfig = null;
    this.view = 'main';
    this.applyViewState();

    this.renderResult();

    this.options.onConfigSave?.(next);
    this.options.onExpandedChange?.(this.isExpanded);
  }

  /**
   * 取消设置：回滚到进入设置前的配置，并返回主视图
   */
  private cancelSettings(): void {
    if (this.draftConfig) {
      this.config = { ...this.draftConfig };
    }
    this.draftConfig = null;
    this.view = 'main';
    this.applyViewState();
    this.renderResult();

    // 高度变化（设置面板 -> 主面板）也需要通知外部
    if (this.options.onExpandedChange) {
      this.options.onExpandedChange(this.isExpanded);
    }
  }

  private syncSettingsFormFromConfig(config: MeasureConfig): void {
    this.unitSelectEl.value = config.unit;
    this.precisionSelectEl.value = String(config.precision);
  }

  private applyViewState(): void {
    if (this.view === 'settings') {
      this.mainViewEl.style.display = 'none';
      // 注意：CSS 里 `.bim-measure-settings { display: none; }` 是默认隐藏
      // 因此这里必须显式设置为可见（否则会出现“进入设置页后什么都不显示”的问题）
      this.settingsViewEl.style.display = 'block';
    } else {
      // 显式恢复主视图显示（避免外部样式干扰）
      this.mainViewEl.style.display = 'block';
      this.settingsViewEl.style.display = 'none';
    }
  }

  /**
   * 从缓存读取配置
   * - 有缓存：返回解析后的配置
   * - 无缓存/解析失败：返回 null
   */
  private loadConfigFromCache(): MeasureConfig | null {
    try {
      const raw = localStorage.getItem(MeasurePanel.CONFIG_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<MeasureConfig>;
      if (!parsed || typeof parsed !== 'object') return null;

      const unit = parsed.unit;
      const precision = parsed.precision;

      if (!this.isValidUnit(unit) || !this.isValidPrecision(precision as number)) return null;

      return {
        unit,
        precision: precision as MeasurePrecision
      };
    } catch (_e) {
      // localStorage 可能被禁用或 JSON 格式不正确，直接忽略
      return null;
    }
  }

  /**
   * 写入缓存（localStorage）
   */
  private saveConfigToCache(config: MeasureConfig): void {
    try {
      localStorage.setItem(MeasurePanel.CONFIG_CACHE_KEY, JSON.stringify(config));
    } catch (_e) {
      // localStorage 可能被禁用：忽略即可，不影响功能
    }
  }

  private isValidUnit(unit: any): unit is MeasureUnit {
    return unit === 'm' || unit === 'cm' || unit === 'mm' || unit === 'km';
  }

  private isValidPrecision(precision: any): precision is MeasurePrecision {
    return precision === 0 || precision === 1 || precision === 2 || precision === 3;
  }

  /**
   * 应用“展开/收起”状态：默认只显示前 4 个按钮
   */
  private applyExpandedState(): void {
    let index = 0;
    for (const btn of this.toolButtons.values()) {
      // 默认展示前四个，其余根据展开状态显示/隐藏
      if (index >= 4) {
        btn.style.display = this.isExpanded ? '' : 'none';
      } else {
        btn.style.display = '';
      }
      index++;
    }

    // toggle 样式（旋转箭头）
    if (this.isExpanded) {
      this.toggleBtn.classList.add('is-expanded');
    } else {
      this.toggleBtn.classList.remove('is-expanded');
    }
  }

  /**
   * 应用"当前选中按钮"样式
   */
  private applyActiveModeState(): void {
    for (const [mode, btn] of this.toolButtons.entries()) {
      if (mode === this.activeMode) {
        btn.classList.add('is-active');
      } else {
        btn.classList.remove('is-active');
      }
    }
  }

  public clearActiveMode(): void {
    for (const btn of this.toolButtons.values()) {
      btn.classList.remove('is-active');
    }
    this.activeMode = null;
  }

  /**
   * 渲染结果区（根据 activeMode 从 result 里取对应字段）
   */
  private renderResult(): void {
    // 1) 根据模式决定结果区显示规则
    // 你给的规则：
    // - 距离：只显示数值
    // - 最小距离：只显示数值
    // - 角度：--°
    // - 标高：--m（固定 m）
    // - 体积：--mm³（单位随设置变动，即 unit³）
    // - 激光测距：不显示任何数值/xyz，只显示“激光测距”文字
    // - 坡度：--%
    // - 空间体积：--mm³（单位随设置变动，即 unit³）

    const isPointMode = this.activeMode === 'point';
    
    if (isPointMode) {
      this.mainValueRowEl.style.display = 'none';
      this.xyzBoxEl.style.display = '';
      const xyz = this.result?.xyz;
      if (!xyz) {
        this.xyzXEl.textContent = '--';
        this.xyzYEl.textContent = '--';
        this.xyzZEl.textContent = '--';
        return;
      }
      this.xyzXEl.textContent = this.convertValue('length', xyz.x) + ' ' + this.getUnitText('length');
      this.xyzYEl.textContent = this.convertValue('length', xyz.y) + ' ' + this.getUnitText('length');
      this.xyzZEl.textContent = this.convertValue('length', xyz.z) + ' ' + this.getUnitText('length');
      return;
    }

    this.mainValueRowEl.style.display = '';
    if (!this.activeMode) {
      this.mainNumberEl.textContent = '--';
      this.mainUnitEl.textContent = '';
      this.xyzBoxEl.style.display = 'none';
      return;
    }
    this.mainValueLabelEl.textContent = t(this.getModeValueLabelI18nKey(this.activeMode));
    const value = this.result ? (this.result as any)[MEASURE_TYPES[this.activeMode].callBackType] : undefined;
    
    if (this.activeMode === 'slope'||this.activeMode === 'angle') {
      this.mainNumberEl.textContent = value ?? '--';
      this.mainUnitEl.textContent = '';
    } else {
      const valueType = getValueType(this.activeMode);
      this.mainNumberEl.textContent = this.convertValue(valueType, value);
      this.mainUnitEl.textContent = this.getUnitText(valueType);
    }
    this.xyzBoxEl.style.display = 'none';
  }

  private getModeI18nKey(mode: MeasureMode): string {
    return `measure.modes.${mode}`;
  }

  /**
   * 获取“主值 label”的国际化 key（随模式变化）
   */
  private getModeValueLabelI18nKey(mode: MeasureMode): string {
    return `measure.labels.value.${mode}`;
  }

  /**
   * 统一的数值转换方法
   * @param type 测量值类型：length(长度)、area(面积)、angle(角度)、percent(百分比)、point(坐标)
   * @param value 原始数值（单位：长度为米，面积为平方米）
   * @returns 转换后的格式化字符串，无效值返回 '--'
   */
  private convertValue(type: MeasureValueType, value: number | undefined | null): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '--';
    }

    const unit = this.config.unit;
    const precision = this.config.precision;
    let converted: number;

    switch (type) {
      case 'length':
        switch (unit) {
          case 'mm': converted = value * 1000; break;
          case 'cm': converted = value * 100; break;
          case 'km': converted = value / 1000; break;
          default: converted = value;
        }
        break;
      case 'area':
        switch (unit) {
          case 'mm': converted = value * 1000 * 1000; break;
          case 'cm': converted = value * 100 * 100; break;
          case 'km': converted = value / 1000 / 1000; break;
          default: converted = value;
        }
        break;
      case 'angle':
      case 'percent':
      case 'point':
      default:
        converted = value;
    }

    return converted.toFixed(precision);
  }

  /**
   * 获取单位文本
   * @param type 测量值类型
   * @returns 对应的单位文本（如 mm、m²、°）
   */
  private getUnitText(type: MeasureValueType): string {
    const unit = this.config.unit;
    switch (type) {
      case 'length':
      case 'point':
        return unit;
      case 'area':
        return `${unit}²`;
      case 'angle':
      case 'percent':
        return '°';
      default:
        return '';
    }
  }

}


